/**
 * AP-10 — FM-WIN-12: TX Message.
 *
 * Zeilenmodell und Vorgabewerte 1:1 aus TxMessageWindow.cpp:267-294. Das
 * zyklische Senden macht der Worker (`txStart`/`txStop`), nicht der
 * Haupt-Thread: nur dort liegt der Adapter, und im Simulationsmodus laeuft der
 * Frame von dort direkt in den Empfangspfad zurueck.
 */

import './viewsD.css';
import { createFloatingPanel } from './filters/FloatingPanel';
import { createButton, createCheckbox, createCombo, setPlayStopState } from '../ui/controls';
import type { AppStore } from '../app/store';
import type { CanConnection } from '../app/connection';

/** `kDefaultRowCount` (TxMessageWindow.h:22). */
const DEFAULT_ROW_COUNT = 5;
const DEFAULT_DATA = '11 22 33 44 55 66 77 88';
const DEFAULT_INTERVAL_MS = '100';
/** `SetTimer` unter 10 ms lehnt das Original ab (TxMessageWindow.cpp:221). */
const MIN_INTERVAL_MS = 10;
const MAX_DATA_BYTES = 8;

/** Index 0 = "Auto", 1..9 = DLC 0..8 (TxMessageWindow.cpp:276-281). */
const DLC_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  ...Array.from({ length: 9 }, (_, i) => ({ value: String(i), label: String(i) })),
];

interface TxRow {
  /** Stabil, einmal vergeben, nie neu benutzt (TxMessageWindow.h:33). */
  readonly id: number;
  idInput: HTMLInputElement;
  dataInput: HTMLInputElement;
  dlcSelect: HTMLSelectElement;
  intervalInput: HTMLInputElement;
  extended: boolean;
  remote: boolean;
  running: boolean;
  playButton: HTMLButtonElement;
  element: HTMLElement;
}

export interface TxMessageHandle {
  show(): void;
}

/**
 * Leerzeichengetrennte Hexbytes, hoechstens 8. Ein ungueltiges Feld ist ein
 * Fehler, kein Listenende: `OnSendClicked` bricht dort mit
 * `Send error: invalid data byte '<hex>'` ab (CanMonitorApp.cpp:2128).
 */
export function parseDataBytes(text: string): { bytes: number[] } | { invalidToken: string } {
  const bytes: number[] = [];
  for (const token of text.trim().split(/\s+/)) {
    if (token === '') break;
    if (bytes.length === MAX_DATA_BYTES) break;
    if (!/^[0-9a-fA-F]+$/.test(token)) return { invalidToken: token };
    bytes.push(Number.parseInt(token, 16) & 0xff);
  }
  return { bytes };
}

/** Hex wie `std::stoul(idStr, nullptr, 16)`; `null` = ungueltig. */
export function parseFrameId(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '' || !/^[0-9a-fA-F]+$/.test(trimmed)) return null;
  const value = Number.parseInt(trimmed, 16);
  return Number.isFinite(value) ? value >>> 0 : null;
}

function createTextInput(value: string, className: string): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = `cm-txt ${className}`;
  input.value = value;
  input.spellcheck = false;
  return input;
}

export function createTxMessageView(store: AppStore, connection: CanConnection): TxMessageHandle {
  const panel = createFloatingPanel('TX Message');
  panel.element.classList.add('cm-panel-window--wide');

  const rows: TxRow[] = [];
  let nextRowId = 0;

  const header = document.createElement('div');
  header.className = 'cm-txrow cm-txrow--head';
  for (const [label, cls] of [
    ['ID (hex)', 'cm-tx-id'],
    ['Data (hex bytes)', 'cm-tx-data'],
    ['DLC', 'cm-tx-dlc'],
    ['Int.(ms)', 'cm-tx-int'],
    ['Ext', 'cm-tx-flag'],
    ['RTR', 'cm-tx-flag'],
    ['', 'cm-tx-actions'],
  ] as const) {
    const cell = document.createElement('span');
    cell.className = cls;
    cell.textContent = label;
    header.append(cell);
  }

  const list = document.createElement('div');
  list.className = 'cm-txlist';

  const addButton = createButton('+ Add Frame', { onClick: () => addRow() });

  const layout = document.createElement('div');
  layout.className = 'cm-view';
  layout.append(header, list, addButton);
  panel.body.append(layout);

  function setStatus(text: string): void {
    store.emit('status:changed', { text });
  }

  function buildFrame(row: TxRow): { id: number; data: number[]; dlc: number } | null {
    const id = parseFrameId(row.idInput.value);
    if (id === null) {
      setStatus('Send error: invalid ID');
      return null;
    }
    const parsed = parseDataBytes(row.dataInput.value);
    if ('invalidToken' in parsed) {
      setStatus(`Send error: invalid data byte '${parsed.invalidToken}'`);
      return null;
    }
    const bytes = parsed.bytes;
    // "Auto" leitet die Laenge aus den eingegebenen Bytes ab, ein fester Wert
    // gewinnt und laesst den Rest auf 0 (CanMonitorApp.cpp:2141-2142).
    const dlc = row.dlcSelect.value === 'auto' ? bytes.length : Number(row.dlcSelect.value);
    const data = new Array<number>(dlc).fill(0);
    for (let i = 0; i < dlc && i < bytes.length; i += 1) data[i] = bytes[i] ?? 0;
    return { id, data, dlc };
  }

  function periodOf(row: TxRow): number {
    const parsed = Number.parseInt(row.intervalInput.value, 10);
    return Number.isFinite(parsed) ? Math.max(MIN_INTERVAL_MS, parsed) : Number(DEFAULT_INTERVAL_MS);
  }

  async function togglePlay(row: TxRow): Promise<void> {
    if (row.running) {
      await connection.txStop(row.id);
      row.running = false;
      setPlayStopState(row.playButton, false);
      return;
    }
    // Der Verbindungstest steht im Original VOR dem Auswerten der Eingaben
    // (CanMonitorApp.cpp:2102), sonst meldet eine ungueltige ID den falschen
    // Grund.
    if (!store.settings.simulationMode && !store.connected) {
      setStatus('Send blocked: connect first');
      return;
    }
    const frame = buildFrame(row);
    if (frame === null) return;
    try {
      await connection.txStart(
        row.id,
        { id: frame.id, data: frame.data, dlc: frame.dlc, extended: row.extended, remote: row.remote },
        periodOf(row),
      );
      row.running = true;
      setPlayStopState(row.playButton, true);
      // `ToggleCyclic` sendet sofort einmal mit; die Meldung kommt daher aus
      // `OnSendClicked` und lautet auch beim Start des Zyklus so.
      setStatus(store.settings.simulationMode ? 'Sent 1 frame (simulated)' : 'Sent 1 frame');
    } catch (error) {
      setStatus(`Send failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function removeRow(row: TxRow): void {
    if (row.running) void connection.txStop(row.id);
    row.element.remove();
    const index = rows.indexOf(row);
    if (index >= 0) rows.splice(index, 1);
  }

  function addRow(): TxRow {
    const id = nextRowId;
    nextRowId += 1;

    const element = document.createElement('div');
    element.className = 'cm-txrow';

    // Vorgabe-ID `18FEF1XX` mit XX = laufende Zeilennummer (TxMessageWindow.cpp:267).
    const idInput = createTextInput(
      `18FEF1${(id & 0xff).toString(16).toUpperCase().padStart(2, '0')}`,
      'cm-tx-id',
    );
    const dataInput = createTextInput(DEFAULT_DATA, 'cm-tx-data');
    const dlcSelect = createCombo({ items: DLC_OPTIONS, value: 'auto' });
    dlcSelect.classList.add('cm-tx-dlc');
    const intervalInput = createTextInput(DEFAULT_INTERVAL_MS, 'cm-tx-int');

    const row: TxRow = {
      id,
      idInput,
      dataInput,
      dlcSelect,
      intervalInput,
      extended: true,
      remote: false,
      running: false,
      playButton: createButton('\u25b6', { onClick: () => void togglePlay(row) }),
      element,
    };
    setPlayStopState(row.playButton, false);

    const ext = createCheckbox('', { checked: true, onChange: (v) => (row.extended = v) });
    ext.element.classList.add('cm-tx-flag');
    const rtr = createCheckbox('', { checked: false, onChange: (v) => (row.remote = v) });
    rtr.element.classList.add('cm-tx-flag');

    const actions = document.createElement('span');
    actions.className = 'cm-tx-actions';
    actions.append(row.playButton, createButton('\u2715', { onClick: () => removeRow(row) }));

    element.append(idInput, dataInput, dlcSelect, intervalInput, ext.element, rtr.element, actions);
    list.append(element);
    rows.push(row);
    return row;
  }

  for (let i = 0; i < DEFAULT_ROW_COUNT; i += 1) addRow();

  // Schliessen versteckt nur — laufende Zeilen senden weiter
  // (TxMessageWindow.cpp:505, CanMonitorApp.cpp:2088).
  return { show: () => panel.show() };
}
